import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import { useAdminScope } from "../../context/AdminScopeContext";
import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../../pages/admin/ui_engine";
import { AdminButton } from "../../pages/admin/action_engine";
import { usePanelActions } from "../../pages/admin/panel_engine/consumer";
import {
  DEFAULT_SUPPLIER_PAGE_SIZE,
  createSupplier,
  listSuppliers,
} from "../../services/supplierAdminApi";
import SupplierContractWorkspace from "./supplier_contracts/SupplierContractWorkspace";
import SupplierContactsWorkspace from "./supplier_contacts/SupplierContactsWorkspace";
import SupplierCapabilitiesWorkspace from "./supplier_capabilities/SupplierCapabilitiesWorkspace";
import SupplierDocumentsWorkspace from "./supplier_documents/SupplierDocumentsWorkspace";
import SupplierCreateForm from "./supplier/SupplierCreateForm";

import {
  AdminFormToolbar,
  AdminPagination,
  AdminSearchInput,
  AdminTable,
  AdminTableViewport,
} from "../../pages/admin/section_engine";

export const SUPPLIER_ADMIN_PANEL_STATUS =
  Object.freeze({
    LOADING: "loading",
    EMPTY: "empty",
    ERROR: "error",
    READY: "ready",
  });

const EMPTY_PAGINATION = Object.freeze({
  page: 1,
  page_size: DEFAULT_SUPPLIER_PAGE_SIZE,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_previous: false,
});

function normalizeSupplierResponse(payload) {
  return {
    items: Array.isArray(payload?.items)
      ? payload.items
      : [],
    pagination: {
      ...EMPTY_PAGINATION,
      ...(payload?.pagination || {}),
    },
  };
}

function resolveErrorMessage(error) {
  return (
    error?.response?.data?.message
    || error?.response?.data?.error_description
    || error?.response?.data?.error
    || error?.message
    || "Impossible de charger les fournisseurs."
  );
}

function formatAmount(value) {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "—";
  }

  return new Intl.NumberFormat(
    "fr-FR",
    {
      style: "currency",
      currency: "EUR",
    },
  ).format(amount);
}

function connectorLabel(supplier) {
  if (!supplier.connector_type) {
    return "Non configuré";
  }

  return supplier.connector_type;
}

export default function SupplierAdminPanel() {
  const {
    selectedCompanyUuid,
    selectedActivityUuid,
  } = useAdminScope();

  const [status, setStatus] = useState(
    SUPPLIER_ADMIN_PANEL_STATUS.LOADING,
  );
  const [suppliers, setSuppliers] = useState([]);
  const [
    selectedSupplier,
    setSelectedSupplier,
  ] = useState(null);
  const [pagination, setPagination] = useState(
    EMPTY_PAGINATION,
  );
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [createMode, setCreateMode] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");

  const hasSupplierScope = Boolean(
    selectedCompanyUuid
    && selectedActivityUuid
  );

  const loadSuppliers = useCallback(
    async ({
      requestedPage = 1,
      requestedSearch = search,
    } = {}) => {
      setStatus(
        SUPPLIER_ADMIN_PANEL_STATUS.LOADING,
      );
      setErrorMessage("");

      try {
        const payload = await listSuppliers({
          companyUuid: selectedCompanyUuid,
          activityUuid: selectedActivityUuid,
          search: requestedSearch,
          page: requestedPage,
          pageSize: DEFAULT_SUPPLIER_PAGE_SIZE,
        });

        const normalized =
          normalizeSupplierResponse(payload);

        setSuppliers(normalized.items);
        setPagination(normalized.pagination);

        setSelectedSupplier((current) => {
          if (!normalized.items.length) {
            return null;
          }

          const preserved = normalized.items.find(
            (supplier) => (
              supplier.uuid === current?.uuid
            ),
          );

          return preserved || normalized.items[0];
        });

        setStatus(
          normalized.items.length
            ? SUPPLIER_ADMIN_PANEL_STATUS.READY
            : SUPPLIER_ADMIN_PANEL_STATUS.EMPTY,
        );
      } catch (error) {
        setSuppliers([]);
        setSelectedSupplier(null);
        setPagination(EMPTY_PAGINATION);
        setErrorMessage(
          resolveErrorMessage(error),
        );
        setStatus(
          SUPPLIER_ADMIN_PANEL_STATUS.ERROR,
        );
      }
    },
    [
      search,
      selectedCompanyUuid,
      selectedActivityUuid,
    ],
  );

  useEffect(() => {
    loadSuppliers({
      requestedPage: 1,
      requestedSearch: search,
    });
  }, [
    loadSuppliers,
    search,
  ]);

  const handleCreateSupplier = useCallback(
    async (payload) => {
      if (!hasSupplierScope) {
        setCreateError(
          "Sélectionnez une société et une activité avant de créer un fournisseur."
        );
        return;
      }

      setCreateLoading(true);
      setCreateError("");

      try {
        const response = await createSupplier(
          payload,
          {
            companyUuid: selectedCompanyUuid,
            activityUuid: selectedActivityUuid,
          },
        );

        const createdSupplier =
          response?.supplier ?? null;

        if (
          !createdSupplier
          || !createdSupplier.uuid
        ) {
          throw new Error(
            "La réponse de création ne contient pas le fournisseur créé."
          );
        }

        await loadSuppliers({
          requestedPage: 1,
          requestedSearch: search,
        });

        setSelectedSupplier(
          createdSupplier
        );
        setCreateMode(false);
        setCreateError("");
      } catch (error) {
        setCreateError(
          resolveErrorMessage(error)
        );
      } finally {
        setCreateLoading(false);
      }
    },
    [
      hasSupplierScope,
      loadSuppliers,
      search,
      selectedActivityUuid,
      selectedCompanyUuid,
    ],
  );

  const actions = useMemo(
    () => [
      {
        key: "create",
        label: createMode
          ? "Création en cours"
          : "Créer un fournisseur",
        icon: "+",
        onClick: () => {
          setCreateError("");
          setCreateMode(true);
        },
        disabled:
          !hasSupplierScope
          || createLoading,
      },
      {
        key: "refresh",
        label:
          status
          === SUPPLIER_ADMIN_PANEL_STATUS.LOADING
            ? "Actualisation en cours"
            : "Actualiser les fournisseurs",
        icon: "↻",
        onClick: () => loadSuppliers({
          requestedPage: pagination.page,
          requestedSearch: search,
        }),
        disabled:
          status
          === SUPPLIER_ADMIN_PANEL_STATUS.LOADING,
      },
    ],
    [
      createLoading,
      createMode,
      hasSupplierScope,
      loadSuppliers,
      pagination.page,
      search,
      status,
    ],
  );

  usePanelActions(actions);

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const normalized = searchInput.trim();

    if (normalized === search) {
      loadSuppliers({
        requestedPage: 1,
        requestedSearch: normalized,
      });
      return;
    }

    setSearch(normalized);
  };

  const handleResetSearch = () => {
    setSearchInput("");

    if (search === "") {
      loadSuppliers({
        requestedPage: 1,
        requestedSearch: "",
      });
      return;
    }

    setSearch("");
  };

  return (
    <SupplierPanelRoot>
      {createMode && (
        <SupplierCreateForm
          onSubmit={handleCreateSupplier}
          onCancel={() => {
            if (createLoading) {
              return;
            }

            setCreateError("");
            setCreateMode(false);
          }}
          loading={createLoading}
          error={createError}
        />
      )}

      {!hasSupplierScope && (
        <ScopeRequirement role="status">
          Sélectionnez une société et une activité
          pour consulter ou créer ses fournisseurs.
        </ScopeRequirement>
      )}

      <AdminFormToolbar onSubmit={handleSearchSubmit}>
        <AdminSearchInput
          type="search"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
          }}
          placeholder="Rechercher un fournisseur"
          aria-label="Rechercher un fournisseur"
        />

        <AdminButton variant="primary" type="submit">
          Rechercher
        </AdminButton>

        {(search || searchInput) && (
          <AdminButton variant="secondary"
            type="button"
            onClick={handleResetSearch}
          >
            Réinitialiser
          </AdminButton>
        )}
      </AdminFormToolbar>

      {status
        === SUPPLIER_ADMIN_PANEL_STATUS.LOADING && (
        <AdminLoadingState
          message="Chargement des fournisseurs..."
        />
      )}

      {status
        === SUPPLIER_ADMIN_PANEL_STATUS.ERROR && (
        <AdminErrorState
          message={errorMessage}
          compact
        />
      )}

      {status
        === SUPPLIER_ADMIN_PANEL_STATUS.EMPTY && (
        <AdminEmptyState
          message={
            search
              ? "Aucun fournisseur ne correspond à cette recherche."
              : "Aucun fournisseur n’est configuré pour le moment."
          }
        />
      )}

      {status
        === SUPPLIER_ADMIN_PANEL_STATUS.READY && (
        <>
          <SupplierSummary>
            <strong>
              {pagination.total}
            </strong>{" "}
            fournisseur
            {pagination.total > 1 ? "s" : ""}
          </SupplierSummary>

          <TableViewport>
            <SupplierTable>
              <thead>
                <tr>
                  <th scope="col">Fournisseur</th>
                  <th scope="col">Connecteur</th>
                  <th scope="col">Commande minimum</th>
                  <th scope="col">Franco</th>
                  <th scope="col">Statut</th>
                  <th scope="col">Contrats</th>
                </tr>
              </thead>

              <tbody>
                {suppliers.map((supplier) => (
                  <tr key={supplier.uuid}>
                    <td>
                      <SupplierIdentity>
                        <strong>
                          {supplier.name}
                        </strong>

                        {supplier.order_email && (
                          <span>
                            {supplier.order_email}
                          </span>
                        )}
                      </SupplierIdentity>
                    </td>

                    <td>
                      <ConnectorBadge
                        $configured={
                          supplier.connector_configured
                        }
                      >
                        {connectorLabel(supplier)}
                      </ConnectorBadge>
                    </td>

                    <td>
                      {formatAmount(
                        supplier.minimum_order_amount,
                      )}
                    </td>

                    <td>
                      {formatAmount(
                        supplier.franco_amount,
                      )}
                    </td>

                    <td>
                      <StatusBadge
                        $active={supplier.is_active}
                      >
                        {supplier.is_active
                          ? "Actif"
                          : "Inactif"}
                      </StatusBadge>
                    </td>

                    <td>
                      <AdminButton
                        variant={
                          selectedSupplier?.uuid
                            === supplier.uuid
                            ? "primary"
                            : "secondary"
                        }
                        type="button"
                        aria-pressed={
                          selectedSupplier?.uuid
                            === supplier.uuid
                        }
                        aria-label={
                          `Gérer les contrats ${supplier.name}`
                        }
                        onClick={() => {
                          setSelectedSupplier(supplier);
                        }}
                      >
                        {selectedSupplier?.uuid
                          === supplier.uuid
                          ? "Sélectionné"
                          : "Gérer"}
                      </AdminButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </SupplierTable>
          </TableViewport>

          <AdminPagination
            previousDisabled={
              !pagination.has_previous
            }
            nextDisabled={!pagination.has_next}
            onPrevious={() => loadSuppliers({
              requestedPage:
                Math.max(
                  1,
                  pagination.page - 1,
                ),
              requestedSearch: search,
            })}
            onNext={() => loadSuppliers({
              requestedPage:
                pagination.page + 1,
              requestedSearch: search,
            })}
            label={`Page ${pagination.page} / ${Math.max(
              pagination.total_pages,
              1,
            )}`}
          />

          {selectedSupplier && (
            <>
              <ContractWorkspaceSection>
                <SupplierContractWorkspace
                  key={selectedSupplier.uuid}
                  supplierUuid={selectedSupplier.uuid}
                  supplierLabel={selectedSupplier.name}
                />
              </ContractWorkspaceSection>

              <ContactWorkspaceSection>
                <SupplierContactsWorkspace
                  supplierUuid={
                    selectedSupplier.uuid
                  }
                  supplierName={
                    selectedSupplier.trade_name
                    || selectedSupplier.name
                    || ""
                  }
                />

                <SupplierCapabilitiesWorkspace
                  supplierUuid={
                    selectedSupplier.uuid
                  }
                  supplierName={
                    selectedSupplier.trade_name
                    || selectedSupplier.name
                  }
                />
              </ContactWorkspaceSection>

              <DocumentWorkspaceSection>
                <SupplierDocumentsWorkspace
                  supplierUuid={
                    selectedSupplier.uuid
                  }
                  supplierName={
                    selectedSupplier.trade_name
                    || selectedSupplier.name
                    || ""
                  }
                />
              </DocumentWorkspaceSection>
            </>
          )}
        </>
      )}
    </SupplierPanelRoot>
  );
}

const ScopeRequirement = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid
    rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  background:
    rgba(255, 255, 255, 0.05);
  color:
    rgba(247, 247, 247, 0.72);
`;

const SupplierPanelRoot = styled.section`
  min-width: 0;
  color: #f7f7f7;
`;

const SupplierSummary = styled.p`
  margin: 0 0 0.75rem;
  color: rgba(247, 247, 247, 0.68);
`;

const TableViewport = styled(AdminTableViewport)`
  width: 100%;
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
`;

const SupplierTable = styled(AdminTable)`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  background: #202a43;

  th,
  td {
    padding: 0.85rem;
    border-bottom:
      1px solid rgba(255, 255, 255, 0.07);
    text-align: left;
    vertical-align: middle;
  }

  th {
    color: rgba(247, 247, 247, 0.62);
    font-size: 0.76rem;
    font-weight: 850;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

const SupplierIdentity = styled.div`
  display: grid;
  gap: 0.25rem;

  span {
    color: rgba(247, 247, 247, 0.58);
    font-size: 0.8rem;
  }
`;

const ConnectorBadge = styled.span`
  display: inline-flex;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  background:
    ${({ $configured }) => (
      $configured
        ? "rgba(73, 184, 126, 0.16)"
        : "rgba(255, 255, 255, 0.08)"
    )};
  color:
    ${({ $configured }) => (
      $configured
        ? "#8ce0b2"
        : "rgba(247, 247, 247, 0.65)"
    )};
  font-size: 0.75rem;
  font-weight: 800;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  background:
    ${({ $active }) => (
      $active
        ? "rgba(73, 184, 126, 0.16)"
        : "rgba(225, 95, 95, 0.16)"
    )};
  color:
    ${({ $active }) => (
      $active
        ? "#8ce0b2"
        : "#ffaaaa"
    )};
  font-size: 0.75rem;
  font-weight: 850;
`;

const ContactWorkspaceSection = styled.section`
  margin-top: 1rem;
`;

const ContractWorkspaceSection = styled.section`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid
    rgba(255, 255, 255, 0.09);
`;


const DocumentWorkspaceSection = styled.section`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid
    rgba(255, 255, 255, 0.09);
`;
